import { useEffect, useState } from "react";
import { Pencil, Plus, RadioTower } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const empty = { name: "", strategy: "", description: "", image_url: "", pairs: "", coin_price: "", status: "COMING_SOON" };

function ProviderForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } }); setF((x) => ({ ...x, image_url: data.url })); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const save = async () => {
    if (busy) return;
    setBusy(true);
    const body = { name: f.name, strategy: f.strategy, description: f.description, image_url: f.image_url || null,
      pairs: f.pairs.split(",").map((s) => s.trim()).filter(Boolean), coin_price: Number(f.coin_price) };
    if (f.id) body.status = f.status;
    try {
      if (f.id) await api.patch(`/admin/signal-providers/${f.id}`, body); else await api.post("/admin/signal-providers", body);
      toast.success(f.id ? "Provider updated" : "Provider created (COMING SOON)"); onSaved();
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="sp-admin-form">
      <div className="card-lux w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-5 space-y-3 rounded-t-2xl sm:rounded-2xl">
        <h2 className="text-white font-semibold">{f.id ? "Edit Provider" : "New Provider"}</h2>
        {f.id && <p className="text-[10px] font-mono text-[#64748B]">Provider ID: {f.id}</p>}
        <input value={f.name} onChange={set("name")} placeholder="Provider name" className="input-lux" data-testid="sp-form-name" />
        <input value={f.strategy} onChange={set("strategy")} placeholder="Strategy name / summary" className="input-lux" data-testid="sp-form-strategy" />
        <textarea value={f.description} onChange={set("description")} rows={3} placeholder="Description / details" className="input-lux" data-testid="sp-form-description" />
        <input value={f.pairs} onChange={set("pairs")} placeholder="Pairs / instruments, comma separated (e.g. XAUUSD, USOIL)" className="input-lux" data-testid="sp-form-pairs" />
        <input type="number" min="0.01" step="0.01" value={f.coin_price} onChange={set("coin_price")} placeholder="Unlock price (coins)" className="input-lux" data-testid="sp-form-price" />
        <div className="flex items-center gap-3">
          {f.image_url && <img src={f.image_url} alt="" className="h-14 w-14 rounded-lg object-cover border border-[#222530]" />}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="text-xs text-[#94A3B8]" data-testid="sp-form-image" />
        </div>
        {f.id ? (
          <select value={f.status} onChange={set("status")} className="input-lux" data-testid="sp-form-status">
            <option value="COMING_SOON">COMING SOON</option><option value="LIVE">LIVE</option>
          </select>
        ) : <p className="text-[11px] text-[#64748B]">New providers start as COMING SOON. Switch to LIVE manually once its indicator is deployed and approved.</p>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={busy} className="btn-outline-gold flex-1 py-2.5 text-sm" data-testid="sp-form-cancel">Cancel</button>
          <button onClick={save} disabled={busy || !f.name || !f.coin_price} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-50" data-testid="sp-form-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSignalProviders() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/signal-providers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-5" data-testid="admin-signal-providers-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h1 className="page-title">Signal Providers</h1><p className="page-sub mt-1">Provider cards, pricing and LIVE status</p></div>
        <button onClick={() => setEdit(empty)} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2" data-testid="sp-admin-add"><Plus className="h-4 w-4" /> New Provider</button>
      </div>
      {items.length === 0 ? (
        <div className="card-lux p-10 text-center" data-testid="sp-admin-empty"><RadioTower className="h-8 w-8 text-[#D4AF37]/40 mx-auto mb-3" /><p className="text-sm text-[#64748B]">No providers yet.</p></div>
      ) : (
        <div className="card-lux divide-y divide-[#222530]">
          {items.map((p) => (
            <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`sp-admin-row-${p.id}`}>
              <div className="h-12 w-12 rounded-lg bg-[#0B0C10] border border-[#222530] overflow-hidden shrink-0">{p.image_url && <img src={p.image_url} alt="" className="h-full w-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm truncate">{p.name} <span className="text-[#64748B] font-normal">· {p.strategy}</span></p>
                <p className="text-xs text-[#94A3B8] truncate">{p.pairs.join(", ") || "—"} · {(p.coin_price_minor / 100).toFixed(2)} coins · 30 days · <span className="font-mono text-[10px]">{p.id}</span></p>
              </div>
              <span className={`text-[9px] font-mono tracking-widest rounded-full px-2 py-0.5 border ${p.status === "LIVE" ? "border-emerald-400/40 text-emerald-400" : "border-[#D4AF37]/40 text-[#D4AF37]"}`} data-testid={`sp-admin-status-${p.id}`}>{p.status.replace("_", " ")}</span>
              <button onClick={() => setEdit({ ...p, image_url: p.image_url || "", pairs: p.pairs.join(", "), coin_price: (p.coin_price_minor / 100).toString() })} className="text-[#94A3B8] hover:text-white" aria-label="Edit" data-testid={`sp-admin-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {edit && <ProviderForm initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
